import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import TopbarInner from "../components/topbar/TopbarInner";
import MoodSlider from "../components/ai/MoodSlider";
import { composeAuriaMoment } from "../services/ai/composer/sessionComposer.js";
import { getStateScale } from "../services/ai/domain/stateScales.js";
import { normalizeDailyIntent } from "../services/breathing/dailyBreathing.js";
import "./RefugePage.css";

/* Le Refuge compose toujours un arc de dix minutes. C'est une implémentation
   interne : cette durée n'est jamais présentée comme une cible à atteindre. */
const REFUGE_DURATION_KEY = "10min";
const MAX_EXCLUDED_TRACKS = 4;
const MAX_EXCLUDED_BREATHINGS = 2;

function rotationKey(uid) {
  return `auria:refuge-rotation:${uid || "anonymous"}`;
}

function readRotation(uid) {
  if (typeof window === "undefined" || !uid) return { trackIds: [], breathingIds: [] };

  try {
    const parsed = JSON.parse(localStorage.getItem(rotationKey(uid)) || "{}");
    return {
      trackIds: Array.isArray(parsed.trackIds) ? parsed.trackIds.slice(-MAX_EXCLUDED_TRACKS) : [],
      breathingIds: Array.isArray(parsed.breathingIds) ? parsed.breathingIds.slice(-MAX_EXCLUDED_BREATHINGS) : [],
    };
  } catch {
    return { trackIds: [], breathingIds: [] };
  }
}

function writeRotation(uid, session) {
  if (typeof window === "undefined" || !uid || !session) return;

  const current = readRotation(uid);
  const nextTrackIds = [...new Set([...current.trackIds, ...(session.arc?.tracks?.map((track) => track.id).filter((id) => id != null) || [])])].slice(-MAX_EXCLUDED_TRACKS);
  const nextBreathingIds = [...new Set([...current.breathingIds, ...(session.breathing?.id != null ? [session.breathing.id] : [])])].slice(-MAX_EXCLUDED_BREATHINGS);

  try {
    localStorage.setItem(rotationKey(uid), JSON.stringify({ trackIds: nextTrackIds, breathingIds: nextBreathingIds }));
  } catch {
    /* noop */
  }
}

function announcePlayerTransition() {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new Event("harmonauria:player-transition-start"));
}

export default function RefugePage({ user = null, isPremium = false, preferredIntent = "", naturalSounds = [], favoriteNatureSoundIds = [], currentStreak = 0, onBeforePlayerLaunch, onPrepareAuriaSession }) {
  const navigate = useNavigate();
  const [stateBefore, setStateBefore] = useState(5);
  const [isLaunching, setIsLaunching] = useState(false);
  const [error, setError] = useState("");
  const launchGuardRef = useRef(false);
  const intentKey = normalizeDailyIntent(preferredIntent);
  const stateScale = useMemo(() => getStateScale(intentKey), [intentKey]);

  useEffect(() => {
    if (isPremium || !user) return;
    navigate("/", { replace: true });
  }, [isPremium, navigate, user]);

  const composeWithRotation = useCallback(async () => {
    const rotation = readRotation(user?.uid);
    const basePayload = { uid: user.uid, intent: intentKey, durationKey: REFUGE_DURATION_KEY, stateBefore, naturalSounds, favoriteNatureSoundIds, currentStreak, regenerationCount: 0, saveStart: false };

    try {
      return await composeAuriaMoment({ ...basePayload, excludeTrackIds: rotation.trackIds, excludeBreathingIds: rotation.breathingIds });
    } catch (firstError) {
      if (!rotation.trackIds.length && !rotation.breathingIds.length) throw firstError;
      return composeAuriaMoment({ ...basePayload, excludeTrackIds: rotation.trackIds.slice(-1), excludeBreathingIds: rotation.breathingIds.slice(-1) });
    }
  }, [currentStreak, favoriteNatureSoundIds, intentKey, naturalSounds, stateBefore, user?.uid]);

  const startRefuge = useCallback(async () => {
    if (!isPremium || !user?.uid || launchGuardRef.current) return;
    launchGuardRef.current = true;
    setIsLaunching(true);
    setError("");

    try {
      try {
        await onBeforePlayerLaunch?.();
      } catch {
        /* Le player retentera au montage. */
      }

      const result = await composeWithRotation();
      const session = result?.session;
      if (!session?.arc?.tracks?.[0]?.file) throw new Error("Aucune piste disponible pour ce Refuge");

      const finalSession = {
        ...session,
        context: {
          ...(session.context || {}),
          withBreathing: Boolean(session.breathing),
          withNaturals: Boolean(session.ambiances?.length),
        },
      };

      writeRotation(user.uid, finalSession);
      const payload = { session: finalSession, stateBefore, intent: intentKey, withBreathing: finalSession.context.withBreathing };
      announcePlayerTransition();

      if (onPrepareAuriaSession) {
        const ready = await onPrepareAuriaSession(payload);
        if (ready === false) throw new Error("Auria session preparation failed");
        return;
      }

      navigate("/ia/player", { state: { ...payload } });
    } catch (launchError) {
      console.warn("Refuge direct launch failed", launchError);
      setError("Votre Refuge n’a pas pu être lancé. Vous pouvez réessayer.");
      setIsLaunching(false);
      launchGuardRef.current = false;
    }
  }, [composeWithRotation, intentKey, isPremium, navigate, onBeforePlayerLaunch, onPrepareAuriaSession, stateBefore, user?.uid]);

  if (!user || !isPremium) return null;

return (
  <div className="refuge-screen">
    <TopbarInner
      titleColor="#36495B"
      isPremium={isPremium}
      onBack={() => navigate("/")}
    />

    <main className="refuge-page">
      <header className="page-hero">
        <p className="page-hero__eyebrow">Votre Refuge Personnel</p>

        <h1
          id="refuge-page-title"
          className="page-hero__title"
        >
          {stateScale.question || "Où en êtes-vous ?"}
        </h1>

        <p className="page-hero__lead">Votre ressenti suffit. Auria compose la séance à partir de votre objectif et de vos préférences.</p>
      </header>

      <section className="refuge-page__center">
        <div className="refuge-page__mood-section">
          <p className="refuge-page__section-label">Votre ressenti</p>

          <div className="refuge-page__mood">
            <MoodSlider
              intent={intentKey}
              value={stateBefore}
              onChange={setStateBefore}
              variant="before"
            />
          </div>
        </div>

        <button
          type="button"
          className="refuge-page__start"
          onClick={startRefuge}
          disabled={isLaunching}
          aria-busy={isLaunching}
        >
          {isLaunching && <span className="refuge-page__spinner" aria-hidden="true" />}
          <span>{isLaunching ? "Votre Refuge prend forme…" : "Commencer mon Refuge"}</span>
          {!isLaunching && <ArrowRight size={18} strokeWidth={2.4} aria-hidden="true" />}
        </button>

        <p className="refuge-page__rotation-note">Auria renouvelle les créations proposées pour éviter de vous ramener toujours au même paysage sonore.</p>

        <div
          className="refuge-page__status"
          role="status"
          aria-live="polite"
        >
          {error}
        </div>
      </section>
    </main>
  </div>
);
}
